import licenseHistoryStyle from './license-history.module.css';

import Button from 'components/Button';
import Breadcrumbs from 'components/Breadcrumb';
import InlineTitle from 'components/InlineTitle';

import TooltipDialog from 'components/TooltipDialog';
import InputWithIcon from 'components/InputWithIcon';

import PaginationContainer from 'components/PaginationContainer';
import DropdownWithTimeframe, { getTimeFrameSetting, getTimeFrameKey } from 'components/DropdownWithTimeframe';
import MessageBoxGroup from "components/MessageBoxGroup";

import LicenseHistoryTableMSP from './components/msp/LicenseHistoryTableMSP';
import LicenseHistoryTableORG from './components/org/LicenseHistoryTableORG';
import GenerateModal from './components/msp/GenerateModal';

import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from 'react-i18next';
import { selectOrgLevel } from 'stores/slice/pageContext';

import licenseService from "services/Auth.License.service";

import { uiActions } from "stores/slice/ui";
import controller from "./controller";
import {
  defaultMessages,
  defaultSearch,
  defaultPagination,
  defaultColumns
} from './const';
import Nuclias from "const/nuclias";
import { getTimeframe } from "utils/date-time.util";
import { PATH } from 'router/config/const';

import dayjs from "dayjs";
import { size, isNil, cloneDeep } from "lodash";
import { jsonToCsv, fromUrl } from "utils/downloadFile.util";

const defaultPathList = [
  { label: '6525f0afe7', isLink: false },
  { label: '40be8113d7', isLink: true, path: PATH.settings.license_management },
  { label: '16d2b386b2', isLink: false },
];

const LicenseHistory = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const pageContext = useSelector(state => state.pageContext);
  const isOrgLevel = useSelector(selectOrgLevel) === Nuclias.organizationType.org ? true : false;
  const orgId = isOrgLevel ? pageContext.selectedOrg.id : pageContext.si.id;
  const customTimeframe = getTimeframe(getTimeFrameSetting(), getTimeFrameKey().last24hrs);

  const [messages, setMessages] = useState(cloneDeep(defaultMessages));
  const [pagination, setPagination] = useState(cloneDeep(defaultPagination));
  const [dataList, setDataList] = useState([]);
  const [search, setSearch] = useState(cloneDeep(defaultSearch));
  // modal
  const [isOpenGenerateModal, setOpenGenerateModal] = useState(false);

  const init = () => {
    setMessages(defaultMessages);
    setPagination(defaultPagination);
    setDataList([]);
    setSearch(defaultSearch);
    setOpenGenerateModal(false);
  };

  const getData = async (newPagination) => {
    try {
      dispatch(uiActions.showSpinner());
      const page = newPagination || pagination;
      const params = controller.getFilter({ isOrgLevel, orgId, }, page, search);
      const res = await controller.getHistoryData(params);
      if (!isOrgLevel) {
        setPagination({ ...pagination, currentLength: res.total });
      }
      setDataList(res.data);
    } catch (error) {
      console.debug('license getData:', error);
    } finally {
      dispatch(uiActions.hideSpinner());
    }
  };

  const changeTimeFrame = (newTimeFrame) => {
    setSearch({ ...search, timeframe: newTimeFrame });
  };

  const handleChangePage = (currentPageNum) => {
    const copy = cloneDeep(pagination);
    copy.current = currentPageNum;
    setPagination(copy);
    if (!isOrgLevel) { getData(copy); }
  };

  const handleChangePageLimit = (currentPageNum) => {
    if (!isOrgLevel) {
      const copy = cloneDeep(defaultPagination);
      copy.pageLimit = currentPageNum;
      setPagination(copy);
      getData(copy);
    } else {
      setPagination({ ...pagination, pageLimit: currentPageNum });
    }
  };

  const downloadORG = useCallback(() => {
    const columns = defaultColumns(isOrgLevel);
    let fields = [];
    let headers = [];
    for (const [colIndex, col] of columns.entries()) {
      headers.push(col.label); // or t(col.label)
      fields.push(col.downloadName || col.accessor);
    }
    const fileName = `${'ORG_License History'}_${dayjs().format(Nuclias.timeFormat.csvDownloadFormat)}.csv`;
    jsonToCsv(fileName, headers, fields, dataList);
  }, [dataList]);

  const downloadMSP = async () => {
    const params = { mspId: pageContext.si.id };
    const res = await licenseService.getMspHistoryGenerateListUrl(params);
    const message = cloneDeep(defaultMessages);
    if (!isNil(res.error)) {
      message.error = res.error.code;
    } else {
      fromUrl(res?.data?.downloadUrl);
    }
    setMessages(message);
  };

  useEffect(() => {
    if (!isNil(search.timeframe)) {
      getData();
    }
  }, [search?.timeframe]);

  useEffect(() => {
    init();
  }, [pageContext]);

  return (
    <>
      <div className="layout-container layout-container--column layout-container--fluid">
        <Breadcrumbs pathList={defaultPathList} />
        <MessageBoxGroup
          messages={messages}
          ErrorMessagesType='es'
          SuccessMessagesType='i18n'
          WarningMessagesType='i18n'
          onClose={() => setMessages(cloneDeep(defaultMessages))}
        />
        <InlineTitle isNonUnderline>
          <span className="form-title">{`${t('a056c9a163')} : `} </span>

          <DropdownWithTimeframe
            forceRerenderObject={pageContext.selectedOrg || pageContext.si}
            customDefaultTimeframe={customTimeframe}
            alignEnd={true}
            onInit={initTimeFrame => changeTimeFrame(initTimeFrame)}
            onChange={selectedTimeframe => changeTimeFrame(selectedTimeframe)}
          />

          <InputWithIcon
            type="search"
            placeholder={t('5d5c4ad5db')}
            iconPosition="left"
            iconClassName="icon-search"
            value={search?.licenseKey}
            onChange={e => setSearch({ ...search, licenseKey: e.target.value })}
            onClick={e => {
              if (size(search?.licenseKey) > 0) {
                setSearch({ ...search, licenseKey: '' });
              }
            }}
          />

          {!isOrgLevel &&
            <Button
              label="Generate"
              className="btn-grey"
              onClick={() => setOpenGenerateModal(true)}
            />
          }

          <Button
            label={t('801ab24683')}
            className="btn-grey"
            onClick={() => {
              isOrgLevel ? downloadORG() : downloadMSP();
            }}
          />

          {!isOrgLevel &&
            <TooltipDialog
              placement="left"
              title={t('9d53acb410')}
            ></TooltipDialog>
          }

        </InlineTitle>

        {!isOrgLevel &&
          <>
            <LicenseHistoryTableMSP {...{ dataList, pagination, search }} />
            <GenerateModal {...{ isOpenGenerateModal, setOpenGenerateModal, search }} />
          </>
        }
        {isOrgLevel && <LicenseHistoryTableORG {...{ dataList, pagination, setPagination, search }} />}

        <PaginationContainer
          total={pagination?.currentLength || 0}
          onPageChange={currentPageNum =>
            handleChangePage(currentPageNum)
          }
          onEntryLimitChange={currentPageNum =>
            handleChangePageLimit(currentPageNum)
          }
          isCurrentPageId={pagination.current}
        />
      </div>
    </>

  );
};

export default LicenseHistory;
